/**
 * TopBar Component
 * LAYER-SHELL: Unified top navigation bar
 *
 * Fixed top navigation bar with logo, help button, and user avatar.
 * Shows portal badge for non-customer portals.
 */
import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bars3Icon, QuestionMarkCircleIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { AvatarMenu } from './AvatarMenu';
import type { UserRole } from './Shell';
import './TopBar.scss';

const GET_CART_ITEMS = gql`
  query getCartTransactions {
    getCartTransactions {
      uuid
      code
      scenarioType
      certificates {
        symbol
      }
    }
  }
`;

interface TopBarProps {
  onMenuClick: () => void;
  onHelpClick: () => void;
  isMobile: boolean;
  userRole: UserRole;
  menuButtonRef?: React.RefObject<HTMLButtonElement>;
  onLogout?: () => void;
}

/**
 * Get portal badge text based on user role.
 * Returns null for customers (no badge needed).
 */
function getPortalBadge(role: UserRole): string | null {
  switch (role) {
    case 'CASE_MANAGER':
      return 'Case Manager';
    case 'COMPLIANCE':
      return 'Compliance';
    case 'ADMIN':
    case 'SUPER_ADMIN':
      return 'Admin';
    case 'SUPPORT':
      return 'Support';
    case 'VIEWER':
      return 'Viewer';
    default:
      return null;
  }
}

/**
 * Get home path based on user role.
 */
function getHomePath(role: UserRole): string {
  switch (role) {
    case 'CUSTOMER':
      return '/accounts/me';
    case 'CASE_MANAGER':
    case 'COMPLIANCE':
      return '/case-manager/dashboard';
    case 'ADMIN':
    case 'SUPER_ADMIN':
    case 'SUPPORT':
    case 'VIEWER':
      return '/admin/transactions';
    default:
      return '/accounts/me';
  }
}

export const TopBar: React.FC<TopBarProps> = ({
  onMenuClick,
  onHelpClick,
  isMobile,
  userRole,
  menuButtonRef,
  onLogout,
}) => {
  const badge = getPortalBadge(userRole);
  const homePath = getHomePath(userRole);

  return (
    <header className="topbar" role="banner">
      <div className="topbar__left">
        {isMobile && (
          <button
            ref={menuButtonRef}
            className="topbar__menu-btn"
            onClick={onMenuClick}
            aria-label="Open navigation menu"
            aria-expanded="false"
          >
            <Bars3Icon className="topbar__menu-icon" />
          </button>
        )}

        <Link to={homePath} className="topbar__logo-link">
          <img
            src="/assets/images/logos/esig-horizontal.svg"
            alt="eSignature Guarantee"
            className="topbar__logo topbar__logo--light"
          />
          <img
            src="/assets/images/logos/esig-horizontal-white.svg"
            alt="eSignature Guarantee"
            className="topbar__logo topbar__logo--dark"
          />
          {badge && <span className="topbar__portal-badge">{badge}</span>}
        </Link>
      </div>

      <div className="topbar__spacer" />

      <div className="topbar__right">
        {userRole === 'CUSTOMER' && <CartBadge />}

        <button
          className="topbar__help-btn"
          onClick={onHelpClick}
          aria-label="Open help panel"
          title="Help"
        >
          <QuestionMarkCircleIcon className="topbar__help-icon" />
        </button>

        <AvatarMenu userRole={userRole} onLogout={onLogout} />
      </div>
    </header>
  );
};

/**
 * Cart icon with hover dropdown showing items and checkout button.
 * Only rendered for CUSTOMER role.
 */
const CartBadge: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data } = useQuery(GET_CART_ITEMS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000,
  });

  const transactions: any[] = data?.getCartTransactions ?? [];
  const count = transactions.length;

  const handleMouseEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (count > 0) setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="topbar__cart"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <button
        className="topbar__help-btn topbar__cart-btn"
        aria-label={count > 0 ? `Cart: ${count} item${count !== 1 ? 's' : ''}` : 'Cart'}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => navigate('/cart')}
      >
        <ShoppingCartIcon className="topbar__help-icon" />
        {count > 0 && <span className="topbar__cart-badge">{count}</span>}
      </button>

      {open && count > 0 && (
        <div className="topbar__cart-dropdown">
          <p className="topbar__cart-dropdown-title">
            {count} transaction{count !== 1 ? 's' : ''} in cart
          </p>
          <ul className="topbar__cart-list">
            {transactions.map((txn: any) => {
              const symbols = txn.certificates?.map((c: any) => c.symbol).filter(Boolean).join(', ');
              const label = txn.scenarioType
                ? txn.scenarioType.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c: string) => c.toUpperCase())
                : 'Transaction';
              return (
                <li key={txn.uuid} className="topbar__cart-item">
                  <span className="topbar__cart-item-code">{txn.code}</span>
                  <span className="topbar__cart-item-meta">
                    {symbols || label}
                  </span>
                </li>
              );
            })}
          </ul>
          <button
            className="topbar__cart-checkout-btn"
            onClick={() => { setOpen(false); navigate('/cart'); }}
          >
            Go to Checkout
          </button>
        </div>
      )}
    </div>
  );
};

export default TopBar;
